import { useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import { t } from '../i18n'
import { confirmAction, haptic, notify, useBackButton } from '../telegram'
import type { Club, CoachFormat, CoachInput, MyCoach, PlayerMe } from '../types'
import { LANG_NAMES } from './Coaches'

interface Props {
  me: PlayerMe
  onClose: (changed: boolean) => void
}

const FORMATS: CoachFormat[] = ['single', 'split', 'group']
const LEVELS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7]

function num(raw: string): number | null {
  const value = Number(raw.replace(',', '.').trim())
  return raw.trim() !== '' && Number.isFinite(value) && value > 0 ? value : null
}

/** Анкета тренера: заполняет сам тренер, публикует администратор. */
export default function CoachEditor({ me, onClose }: Props) {
  const [loaded, setLoaded] = useState(false)
  const [current, setCurrent] = useState<MyCoach | null>(null)
  const [clubs, setClubs] = useState<Club[]>([])
  const [name, setName] = useState(me.first_name)
  const [bio, setBio] = useState('')
  const [cert, setCert] = useState('')
  const [years, setYears] = useState('')
  const [langs, setLangs] = useState<string[]>([me.language])
  const [clubIds, setClubIds] = useState<number[]>([])
  const [levelMin, setLevelMin] = useState(0)
  const [levelMax, setLevelMax] = useState(5)
  const [formats, setFormats] = useState<CoachFormat[]>(['single'])
  const [prices, setPrices] = useState<Record<CoachFormat, string>>({ single: '', split: '', group: '' })
  const [busy, setBusy] = useState(false)

  useEffect(() => useBackButton(() => onClose(false)), [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  useEffect(() => {
    void Promise.all([api.myCoach(), api.clubs()])
      .then(([coach, list]) => {
        setClubs(list)
        if (coach) {
          setCurrent(coach)
          setName(coach.display_name)
          setBio(coach.bio)
          setCert(coach.certificate ?? '')
          setYears(coach.experience_years != null ? String(coach.experience_years) : '')
          setLangs(coach.languages)
          setClubIds(coach.club_ids)
          setLevelMin(coach.level_min)
          setLevelMax(coach.level_max)
          setFormats(coach.formats)
          setPrices({
            single: coach.price_single != null ? String(coach.price_single) : '',
            split: coach.price_split != null ? String(coach.price_split) : '',
            group: coach.price_group != null ? String(coach.price_group) : '',
          })
        }
      })
      .catch((e) => notify(e instanceof Error ? e.message : 'Ошибка'))
      .finally(() => setLoaded(true))
  }, [])

  const byCity = useMemo(() => {
    const groups = new Map<string, Club[]>()
    for (const club of clubs) groups.set(club.city, [...(groups.get(club.city) ?? []), club])
    return [...groups.entries()].sort(([a], [b]) => (a === me.city ? -1 : b === me.city ? 1 : a.localeCompare(b)))
  }, [clubs, me.city])

  const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value])

  async function save() {
    if (name.trim().length < 2 || bio.trim().length < 30 || !langs.length || !clubIds.length || !formats.length) {
      return notify(t().coachFillAll)
    }
    const body: CoachInput = {
      display_name: name.trim(),
      bio: bio.trim(),
      certificate: cert.trim() || null,
      experience_years: years.trim() ? Math.max(0, Math.min(60, Math.round(Number(years)) || 0)) : null,
      languages: langs,
      club_ids: clubIds,
      level_min: Math.min(levelMin, levelMax),
      level_max: Math.max(levelMin, levelMax),
      formats,
      price_single: formats.includes('single') ? num(prices.single) : null,
      price_split: formats.includes('split') ? num(prices.split) : null,
      price_group: formats.includes('group') ? num(prices.group) : null,
    }
    setBusy(true)
    try {
      const saved = await api.saveMyCoach(body)
      haptic('medium')
      if (saved.status === 'pending') notify(t().coachSubmitted)
      onClose(true)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!(await confirmAction(t().coachDeleteConfirm))) return
    try {
      await api.deleteMyCoach()
      onClose(true)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const noTelegram = !me.telegram_id

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().coachMyProfile}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={() => onClose(false)} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎓 {t().coachMyProfile}</div>
      </div>
      <div className="chat-list">
        {!loaded && <p className="muted">{t().loading}</p>}
        {loaded && (
          <div className="deal-form">
            {current && (
              <div className={`coach-status ${current.status}`} style={{ marginBottom: 6 }}>
                {t().coachStatus[current.status]}
              </div>
            )}
            {noTelegram && <div className="card muted">{t().coachNeedTelegram}</div>}

            <label style={{ marginTop: 0 }}>{t().coachName}</label>
            <input value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />

            <label>{t().coachBio}</label>
            <textarea rows={5} maxLength={1000} value={bio} onChange={(e) => setBio(e.target.value)} />
            <div className="muted small" style={{ marginTop: 4 }}>
              {t().coachBioHint} · {bio.trim().length}/1000
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label>{t().coachCert}</label>
                <input value={cert} maxLength={120} placeholder="Monitor FEP…" onChange={(e) => setCert(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label>{t().coachYearsLabel}</label>
                <input inputMode="numeric" value={years} onChange={(e) => setYears(e.target.value.replace(/\D/g, ''))} />
              </div>
            </div>

            <label>{t().coachLangsLabel}</label>
            <div className="chips">
              {Object.entries(LANG_NAMES).map(([code, label]) => (
                <button key={code} className="chip" aria-pressed={langs.includes(code)} onClick={() => setLangs((l) => toggle(l, code))}>
                  {label}
                </button>
              ))}
            </div>

            <label>{t().coachFormatsLabel}</label>
            {FORMATS.map((f) => (
              <div key={f} className="row" style={{ gap: 10, marginTop: 6 }}>
                <button
                  className="chip"
                  style={{ flex: 1 }}
                  aria-pressed={formats.includes(f)}
                  onClick={() => setFormats((list) => FORMATS.filter((x) => (x === f ? !list.includes(f) : list.includes(x))))}
                >
                  {t().fmtName[f]}
                </button>
                <input
                  style={{ flex: '0 0 92px' }}
                  inputMode="decimal"
                  placeholder="€"
                  disabled={!formats.includes(f)}
                  aria-label={`${t().fmtName[f]}, € ${t().fmtUnit[f]}`}
                  value={prices[f]}
                  onChange={(e) => setPrices((p) => ({ ...p, [f]: e.target.value }))}
                />
                <span className="muted small" style={{ flex: '0 0 84px' }}>
                  {t().fmtUnit[f]}
                </span>
              </div>
            ))}

            <label>{t().coachLevelsLabel}</label>
            <div className="row" style={{ gap: 10 }}>
              <select value={levelMin} onChange={(e) => setLevelMin(Number(e.target.value))}>
                {LEVELS.map((v) => (
                  <option key={v} value={v}>
                    {v.toFixed(1)}
                  </option>
                ))}
              </select>
              <span className="muted">—</span>
              <select value={levelMax} onChange={(e) => setLevelMax(Number(e.target.value))}>
                {LEVELS.map((v) => (
                  <option key={v} value={v}>
                    {v.toFixed(1)}
                  </option>
                ))}
              </select>
            </div>

            <label>{t().coachClubsLabel}</label>
            {byCity.map(([city, list]) => (
              <div key={city} style={{ marginBottom: 6 }}>
                <div className="muted small" style={{ margin: '6px 0 4px' }}>
                  {city}
                </div>
                <div className="chips">
                  {list.map((club) => (
                    <button
                      key={club.id}
                      className="chip"
                      aria-pressed={clubIds.includes(club.id)}
                      onClick={() => setClubIds((ids) => toggle(ids, club.id))}
                    >
                      {club.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <button style={{ width: '100%', marginTop: 16 }} disabled={busy || noTelegram} onClick={save}>
              {current && current.status !== 'rejected' ? t().save : t().coachSubmit}
            </button>
            {current && (
              <button className="danger" style={{ width: '100%', marginTop: 8 }} onClick={remove}>
                {t().coachDelete}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
